import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import pic from "../assets/images/terms.jpg";
import { useNavigate } from "react-router-dom";

const terms = [
  {
    _id: 0,
    title: "Clients",
    para: `Geocode provides services such as custom portfolio websites, ATS-friendly resumes, event posters, branding pages, and project showcases with GitHub integration. 

    • Project Delivery: Our team ensures projects are completed with quality and within the agreed timeframe. Clients must provide accurate requirements and timely feedback.  

    • Payments: Payments must follow the agreed plan. Advance payments may be required before starting work.  

    • Revisions: Clients are entitled to limited revisions. Major changes outside the agreed scope may incur additional costs.  

    • Ownership: After full payment, final deliverables belong to the client. However, reusable components and templates remain property of Geocode.  

    • Refunds: Refunds are only given if the project has not started or if Geocode fails to deliver. No refunds are provided for completed work.`,
    contact: [""],
  },
  {
    _id: 1,
    title: "Students & Interns",
    para: `Geocode offers affordable or free online classes in Frontend (HTML, CSS, JS, React, etc.) and Backend (Python, etc.) with real project involvement.  

    • Purpose: Training is provided at very low cost, mainly to cover company expenses such as hosting and communication. We focus on sharing knowledge and real-world experience, not profit.  

    • Classes: Sessions will be held online for 2–2.5 hours daily. Each student is allocated a syllabus, and progress is monitored regularly.  

    • Effort: Students must attend sincerely and complete daily topics. Each student's effort will be tracked.  

    • Warnings: Students showing carelessness or using classes as timepass will get three warnings. After that, Geocode may remove them from the program.  

    • Learning Speed: Both slow and fast learners are welcome, but effort matters more than speed.  

    • Projects: Students may join real projects with Geocode developers. This helps gain experience but does not guarantee job placement.  

    • Materials: Course resources and project work must not be shared publicly without Geocode’s permission.`,
    contact: [""],
  },
  {
    _id: 2,
    title: "General Conditions",
    para: `• Users must not misuse Geocode’s website, services, or materials.  

    • Geocode is not responsible for:  
      - Failure of students to get a job  
      - Misuse of delivered projects by clients  
      - Technical issues beyond our control  

    • Termination: Geocode reserves the right to refuse service or remove students/clients who violate rules.  

    • Updates: Terms & Conditions may be updated anytime, and continued use of services means acceptance of the updated version.`,
    contact: [
      "Contact Information:",
      "Geocode Team",
      "Email: geocode.team@gmail.com",
      "Phone: +91 9876543210",
      "Location: India (Remote Services)",
    ],
  },
];

const Terms = () => {
  const [activeTab, setActiveTab] = useState(0);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5, staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100, damping: 12 }
    }
  };

  const tabVariants = {
    active: {
      backgroundColor: "#FFD700",
      color: "#000000",
      scale: 1.02,
      transition: { duration: 0.3 }
    },
    inactive: {
      backgroundColor: "#FFFFFF",
      color: "#000000",
      scale: 1,
      transition: { duration: 0.3 }
    }
  };

  const contentVariants = {
    enter: { opacity: 0, y: 20, transition: { duration: 0.3 } },
    center: { opacity: 1, y: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.3 } }
  };

  const navigate = useNavigate();

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="min-h-screen bg-white"
    >
      {/* Hero Section */}
      <motion.section
        variants={itemVariants}
        className="relative h-64 md:h-80 lg:h-96 overflow-hidden"
      >
        <div className="absolute inset-0 bg-black/60 z-10" />
        <img src={pic} alt="Terms Background" className="w-full h-full object-cover" />
        <div className="absolute inset-0 z-20 flex flex-col justify-center items-center text-center px-4">
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4"
          >
            Terms & Conditions
          </motion.h1>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: "100px" }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="h-1 bg-yellow-500"
          />
        </div>

      </motion.section>

      {/* Content Section */}
      <motion.section
        variants={itemVariants}
        className="container mx-auto px-4 py-12 md:py-16 lg:py-20 max-w-4xl"
      >
        {/* Tab Navigation */}
        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 mb-8">
          {terms.map((term, index) => (
            <motion.button
              key={term._id}
              onClick={() => setActiveTab(index)}
              variants={tabVariants}
              initial="inactive"
              animate={activeTab === index ? "active" : "inactive"}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex-1 py-4 px-6 rounded-lg font-semibold text-lg border-2 border-black shadow-lg hover:shadow-xl transition-shadow"
            >
              {term.title}
            </motion.button>
          ))}
        </motion.div>

        {/* Tab Content */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-xl shadow-2xl border-2 border-black overflow-hidden"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              variants={contentVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="p-6 md:p-8 lg:p-10"
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="space-y-6"
              >
                <motion.p
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-gray-800 leading-relaxed text-base md:text-lg whitespace-pre-line"
                >
                  {terms[activeTab].para}
                </motion.p>

                {terms[activeTab].contact[0] && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg"
                  >
                    <ul className="space-y-3">
                      {terms[activeTab].contact.map((item, index) => (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                          className="text-gray-800 flex items-start"
                        >
                          <span className="text-yellow-600 mr-2">•</span>
                          {item}
                        </motion.li>
                      ))}
                    </ul>
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </motion.div>

      </motion.section>
      {/* Footer Section */}
      <motion.div
        variants={itemVariants}
        className=" text-center mb-6 mx-4"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="bg-black text-white p-8 rounded-2xl shadow-2xl"
        >
          <h3 className="text-2xl md:text-3xl font-bold mb-4 text-yellow-400">
            Need Further Assistance?
          </h3>
          <p className="text-gray-300 text-lg mb-6">
            Our team is here to help you understand our cookie policy. Don't hesitate to reach out if you have any questions.
          </p>



         <div className="flex flex-col sm:flex-row gap-4 justify-center items-center w-full px-4">
  <motion.button
    whileHover={{
      scale: 1.05,
      boxShadow: "0 15px 30px -5px rgba(255,215,0,0.3)"
    }}
    whileTap={{ scale: 0.98 }}
    onClick={() => navigate("/")}
    className="w-full sm:w-auto bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900 px-8 py-3 rounded-lg text-lg font-semibold shadow-lg transition-all duration-300"
  >
    Back to Home
  </motion.button>

  <motion.button
    whileHover={{
      scale: 1.05,
      boxShadow: "0 15px 30px -5px rgba(255,215,0,0.3)"
    }}
    whileTap={{ scale: 0.98 }}
    onClick={() => navigate("/contact")}
    className="w-full sm:w-auto bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900 px-8 py-3 rounded-lg text-lg font-semibold shadow-lg transition-all duration-300"
  >
    Contact Us
  </motion.button>
</div>

        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default Terms;
